"use client"

import { itemDefinitions } from "@/data/item-types"
import { ItemThumbnail } from "./item-thumbnail"
import { ItemDetails } from "./item-details"

interface ItemsCodexContentProps {
  selectedItemId: string | null
  onSelectItem: (itemId: string | null) => void
  isEntityUnlocked: (entityId: string) => boolean
}

export const ItemsCodexContent = ({
  selectedItemId,
  onSelectItem,
  isEntityUnlocked,
}: ItemsCodexContentProps) => {
  // Get all items sorted by category and rarity
  const sortedItems = Object.values(itemDefinitions)
    .filter((item) => item.id !== "clone-activation-beacon") // Exclude beacon from codex
    .sort((a, b) => {
      // Sort by category first
      // Same stale-key problem as CATEGORY_LABELS: "scroll"/"equipment" do not
      // exist, so armor and weapons both fell through to 999 and sorted last
      // together instead of in their own groups.
      const categoryOrder = { potion: 1, trinket: 2, armor: 3, weapon: 4 }
      const catA = categoryOrder[a.category as keyof typeof categoryOrder] || 999
      const catB = categoryOrder[b.category as keyof typeof categoryOrder] || 999
      if (catA !== catB) return catA - catB

      // Then by rarity
      // "uncommon" is not a rarity in ItemDefinition; "unique" is, and it is the
      // top tier - without it every unique item tied at 999.
      const rarityOrder = { common: 1, rare: 2, epic: 3, legendary: 4, unique: 5 }
      const rarA = rarityOrder[a.rarity as keyof typeof rarityOrder] || 999
      const rarB = rarityOrder[b.rarity as keyof typeof rarityOrder] || 999
      if (rarA !== rarB) return rarA - rarB

      // Finally by name
      return a.name.localeCompare(b.name)
    })

  // Thumbnails content
  const thumbnailsContent = (
    <>
      {sortedItems.map((item) => {
        const isLocked = !isEntityUnlocked(item.id)
        const isSelected = selectedItemId === item.id

        return (
          <ItemThumbnail
            key={item.id}
            item={item}
            isSelected={isSelected}
            isLocked={isLocked}
            onClick={() => {
              if (!isLocked) {
                onSelectItem(isSelected ? null : item.id)
              }
            }}
          />
        )
      })}
    </>
  )

  // Details content
  const detailsContent = selectedItemId ? (
    <ItemDetails item={itemDefinitions[selectedItemId]} />
  ) : null

  return {
    thumbnailsContent,
    detailsContent,
    hasSelection: selectedItemId !== null,
    total: sortedItems.length,
    discovered: sortedItems.filter((item) => isEntityUnlocked(item.id)).length,
  }
}
